import { Button, Card, CardContent, Grid, Snackbar, SnackbarCloseReason } from "@material-ui/core";
import React, { Suspense, useCallback, useContext, useEffect, useState } from "react";
import { Action, DialogProps } from "../../../../@types/dialogProps.types";
import { SnackbarProps } from "../../../../@types/snackbarAPI.types";
import { Branding, RichTextEditorProps, UploadAdministrationType } from "../../../../@types/uploadAdministration.types";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import { UIContext } from "../../../../contexts";
import { api } from "../../../../utils/api";
import { LazyCustomDialog, LazyCustomFullScreenDialogs, LazyRichTextEditorModal } from "../../../shared";
import SnackbarAPI from "../../../shared/snackbar/SnackbarAPI";

import AddCircleIcon from "@material-ui/icons/AddCircle";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";

import UploadBranding from "./uploadBranding/UploadBranding";
import UploadVideo from "./uploadVideo/UploadVideo";

import "./portalCustomization.css";
import { Alert } from "@material-ui/lab";

const PortalCustomization = (): JSX.Element => {
	const [uploadAdministration, setUploadAdministration] = useState<UploadAdministrationType>({
		statementOfUnderstanding: "",
		registrationHelp: ""
	});
	const [videoFile, setVideoFile] = useState<File | any | string | null>(null);
	const [videoFileUrl, setVideoFileUrl] = useState<string>("");
	const { setDashboardHeader } = useContext(UIContext);
	const [openSaveSnackBar, setOpenSaveSnackBar] = useState(false);
	const [openRichTextEditorModal, setOpenRichTextEditorModal] = useState(false);
	const [branding, setBranding] = useState<Branding>({
		logo: "",
		favicon: ""
	});
	const [SOUProps, setSOUProps] = useState<RichTextEditorProps>({
		initialValue: "<p><strong>Statement of Understanding<strong></p>",
		type: "Statement of Understanding"
	});

	const [registrationHelpProps, setRegistrationHelpProps] = useState<RichTextEditorProps>({
		initialValue: "<p>Registration Help</p>",
		type: "Registration Help"
	});
	const [richTextEditorProps, setRichTextEditorProps] = useState<RichTextEditorProps | null>(null);
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (
			event: React.SyntheticEvent<Element, Event> | Event | React.SyntheticEvent<any, Event>,
			reason?: SnackbarCloseReason | undefined
		) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});
	const [deleteDialogProps, setDeleteDialogProps] = useState<DialogProps>({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const [documentDialogProps, setDocumentDialogProps] = useState({
		openDialog: false,
		title: "Statement of Understanding",
		content: "This is a statement of understanding",
		isParsed: false,
		handleCloseCallback: () =>
			setDocumentDialogProps(
				Object.assign({}, documentDialogProps, {
					openDialog: false
				})
			)
	});

	const getBranding = useCallback(() => {
		setBranding(
			Object.assign(
				{},
				{
					logo: null,
					favicon: null
				}
			)
		);
	}, []);

	const handleRichTextEditorModalOpen = () => setOpenRichTextEditorModal(true);

	const handleRichTextEditorModalClose = () => setOpenRichTextEditorModal(false);

	const handleAddClick = useCallback(
		(type: string) => {
			if (type === "Statement of Understanding")
				setRichTextEditorProps({
					...SOUProps,
					initialValue: ""
				});
			else if (type === "Registration Help")
				setRichTextEditorProps({
					...registrationHelpProps,
					initialValue: ""
				});
			handleRichTextEditorModalOpen();
		},
		[SOUProps, registrationHelpProps]
	);

	const handleEditClick = useCallback(
		(type: string) => {
			if (type === "Statement of Understanding") {
				setRichTextEditorProps(Object.assign({}, SOUProps));
			} else if (type === "Registration Help") {
				setRichTextEditorProps(Object.assign({}, registrationHelpProps));
			}
			handleRichTextEditorModalOpen();
		},
		[SOUProps, registrationHelpProps]
	);

	const handleDeleteDialogProps = useCallback(
		(openDialog: boolean, title: string, content: string, actions: Action[]) => {
			setDeleteDialogProps({
				openDialog,
				title,
				content,
				actions
			});
		},
		[]
	);

	const handleBrandingFileChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, files } = event.target;
			if (files && files[0]) {
				if (name === "logo") {
					setBranding(Object.assign({}, branding, { logo: files[0] }));
				} else if (name === "favicon") {
					setBranding(Object.assign({}, branding, { favicon: files[0] }));
				}
			}
			//setValueChange(true);
			event.target.value = "";
		},
		[branding]
	);

	const handleBrandingDelete = useCallback(
		(name: string) => {
			//const { name, files } = event.target;
			if (name === "logo") {
				setBranding(Object.assign({}, branding, { logo: null }));
			} else if (name === "favicon") {
				setBranding(Object.assign({}, branding, { favicon: null }));
			}
		},
		[branding]
	);

	const handleViewClick = useCallback(
		(type: string) => {
			if (type === "Statement of Understanding") {
				setDocumentDialogProps({
					...documentDialogProps,
					openDialog: true,
					title: "Statement of Understanding",
					content: SOUProps.initialValue
				});
			}
			if (type === "Registration Help") {
				setDocumentDialogProps({
					...documentDialogProps,
					openDialog: true,
					title: "Registration Help",
					content: registrationHelpProps.initialValue
				});
			}
		},
		[SOUProps.initialValue, documentDialogProps, registrationHelpProps.initialValue]
	);

	const handleDeleteClick = useCallback(
		async (type: string) => {
			const openDialog = true;
			const title = "Delete Confirmation";
			const content = "Are you sure you want to delete " + type + "?";

			const actions = [
				{
					label: "Cancel",
					name: "cancel",
					callback: () => setDeleteDialogProps(Object.assign({}, deleteDialogProps, { openDialog: false }))
				},
				{
					label: "Yes",
					name: "accept",
					callback: async () => {
						if (type === "Statement of Understanding") {
							const _uploadAdministration =
								await api.uploadAdminstration.deleteStatementOfUnderstanding();
							if (_uploadAdministration) {
								setUploadAdministration(Object.assign({}, _uploadAdministration));
								setSnackbarAPICallProps(
									Object.assign({}, snackbarAPICallProps, {
										open: true,
										message: `Successfully deleted ${type}`,
										severity: "success"
									})
								);
							}
						}
						if (type === "Registration Help") {
							const _uploadAdministration = await api.uploadAdminstration.deleteRegistrationHelp();
							if (_uploadAdministration) {
								setUploadAdministration(Object.assign({}, _uploadAdministration));
								setSnackbarAPICallProps(
									Object.assign({}, snackbarAPICallProps, {
										open: true,
										message: `Successfully deleted ${type}`,
										severity: "success"
									})
								);
							}
						}
						setDeleteDialogProps(Object.assign({}, deleteDialogProps, { openDialog: false }));
					}
				}
			];
			handleDeleteDialogProps(openDialog, title, content, actions);
		},
		[deleteDialogProps, handleDeleteDialogProps, snackbarAPICallProps]
	);

	const getUploadAdministration = useCallback(async () => {
		const _uploadAdministration = await api.uploadAdminstration.getUploadAdministration();
		if (_uploadAdministration) {
			setUploadAdministration(Object.assign({}, _uploadAdministration));
			setSOUProps((prevSOUProps: RichTextEditorProps) => {
				return Object.assign({}, prevSOUProps, {
					initialValue: _uploadAdministration.statementOfUnderstanding
						? _uploadAdministration.statementOfUnderstanding
						: ""
				});
			});

			setRegistrationHelpProps((prevRegistratrationHelpProps: RichTextEditorProps) => {
				return Object.assign({}, prevRegistratrationHelpProps, {
					initialValue: _uploadAdministration.registrationHelp ? _uploadAdministration.registrationHelp : ""
				});
			});
		}
	}, []);

	const handleCloseSaveSnackBar = () => setOpenSaveSnackBar(false);

	const handleEdit = useCallback(
		async (type: string, content: string) => {
			let _uploadAdministration = null;

			if (type !== "Statement of Understanding" && type !== "Registration Help") return;

			if (type === "Statement of Understanding") {
				const payload = {
					statementOfUnderstanding: content
				};
				if (
					uploadAdministration.statementOfUnderstanding !== null ||
					uploadAdministration.statementOfUnderstanding !== undefined
				) {
					_uploadAdministration = await api.uploadAdminstration.addUploadAdministration(payload);
				} else {
					const { _id } = uploadAdministration;
					_uploadAdministration = await api.uploadAdminstration.editUploadAdministration(payload, _id);
				}
				if (_uploadAdministration) {
					setUploadAdministration(Object.assign({}, _uploadAdministration));
					setSnackbarAPICallProps(
						Object.assign({}, snackbarAPICallProps, {
							open: true,
							message: `Successfully submitted ${type}`,
							severity: "success"
						})
					);
				}
			}

			if (type === "Registration Help") {
				const payload = {
					registrationHelp: content
				};
				if (
					uploadAdministration.registrationHelp !== null ||
					uploadAdministration.registrationHelp !== undefined
				) {
					_uploadAdministration = await api.uploadAdminstration.addUploadAdministration(payload);
				} else {
					const { _id } = uploadAdministration;
					_uploadAdministration = await api.uploadAdminstration.editUploadAdministration(payload, _id);
				}
				if (_uploadAdministration) {
					setUploadAdministration(Object.assign({}, _uploadAdministration));
					setSnackbarAPICallProps(
						Object.assign({}, snackbarAPICallProps, {
							open: true,
							message: `Successfully submitted ${type}`,
							severity: "success"
						})
					);
				}
			}
		},
		[snackbarAPICallProps, uploadAdministration]
	);

	useEffect(() => {
		setSOUProps(
			Object.assign({}, SOUProps, {
				initialValue: uploadAdministration.statementOfUnderstanding
					? uploadAdministration.statementOfUnderstanding
					: ""
			})
		);
		setRegistrationHelpProps(
			Object.assign({}, registrationHelpProps, {
				initialValue: uploadAdministration.registrationHelp ? uploadAdministration.registrationHelp : ""
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadAdministration]);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.upload_administration);
		getUploadAdministration();
		getBranding();
	}, [getBranding, getUploadAdministration, setDashboardHeader]);

	// console.log("VideoFile", videoFile);

	return (
		<div className="upload-administration">
			<Suspense fallback={<div />}>
				<SnackbarAPI snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<Suspense fallback={<div />}>
				<LazyCustomDialog dialogProps={deleteDialogProps} />
			</Suspense>
			<Suspense fallback={<div />}>
				<LazyCustomFullScreenDialogs dialogProps={documentDialogProps} />
			</Suspense>
			<Suspense fallback={<div />}>
				{richTextEditorProps ? (
					<LazyRichTextEditorModal
						openRichTextEditorModal={openRichTextEditorModal}
						richTextEditorProps={richTextEditorProps}
						handleRichTextEditorModalClose={handleRichTextEditorModalClose}
						handleSave={handleEdit}
					/>
				) : null}
			</Suspense>
			<Grid container spacing={10}>
				<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
					<div className="card-header">Statement of Undestanding</div>
					<Card>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleViewClick("Statement of Understanding")}
							>
								<span className="button-label-with-icon" style={{ color: "#ffff" }}>
									View
								</span>
								<span>
									<VisibilityIcon className="button-icon" style={{ color: "#ffff" }} />
								</span>
							</Button>
						</CardContent>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleAddClick("Statement of Understanding")}
							>
								<span className="button-label-with-icon">Add</span>
								<span>
									<AddCircleIcon className="button-icon" />
								</span>
							</Button>
						</CardContent>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleEditClick("Statement of Understanding")}
							>
								<span className="button-label-with-icon">Edit</span>
								<span>
									<EditIcon className="button-icon" />
								</span>
							</Button>
						</CardContent>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleDeleteClick("Statement of Understanding")}
							>
								<span className="button-label-with-icon">Delete</span>
								<span>
									<DeleteIcon className="button-icon" />
								</span>
							</Button>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
					<div className="card-header">Registration Help</div>
					<Card>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleViewClick("Registration Help")}
							>
								<span className="button-label-with-icon">View</span>
								<span>
									<VisibilityIcon className="button-icon" />
								</span>
							</Button>
						</CardContent>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleAddClick("Registration Help")}
							>
								<span className="button-label-with-icon">Add</span>
								<span>
									<AddCircleIcon className="button-icon" />
								</span>
							</Button>
						</CardContent>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleEditClick("Registration Help")}
							>
								<span className="button-label-with-icon">Edit</span>
								<span>
									<EditIcon className="button-icon" />
								</span>
							</Button>
						</CardContent>
						<CardContent className="card-content">
							<Button
								className="button-green card-content-bttn"
								onClick={() => handleDeleteClick("Registration Help")}
							>
								<span className="button-label-with-icon">Delete</span>
								<span>
									<DeleteIcon className="button-icon" />
								</span>
							</Button>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
					<div className="card-header">Logo Upload</div>
					<Card>
						<CardContent className="card-content">
							<UploadBranding
								branding={branding}
								handleFileChange={handleBrandingFileChange}
								handleBrandingDelete={handleBrandingDelete}
							/>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
					<UploadVideo
						videoFile={videoFile}
						setVideoFile={setVideoFile}
						videoFileUrl={videoFileUrl}
						setVideoFileUrl={setVideoFileUrl}
					/>
				</Grid>
			</Grid>
			<Snackbar open={openSaveSnackBar} autoHideDuration={6000} onClose={handleCloseSaveSnackBar}>
				<Alert onClose={handleCloseSaveSnackBar} severity="success">
					Successfully Saved
				</Alert>
			</Snackbar>
		</div>
	);
};

export default PortalCustomization;
